/**
 * EMField - 电磁场计算和可视化类
 * @description 管理电场和磁场源，计算场强和洛伦兹力
 */

import { Vec2 } from 'planck';

/** 库仑常数（简化值，用于游戏模拟） */
const COULOMB_CONSTANT = 8.99e9;

/** 游戏简化常数（避免数值过大） */
const GAME_SCALE_FACTOR = 1e-6;

/** 最小距离阈值，防止场强无限大 */
const MIN_DISTANCE = 0.5;

/**
 * 电荷源配置
 */
export interface ChargeSource {
  /** 电荷唯一标识 */
  id: string;
  /** 电荷位置（物理坐标） */
  position: Vec2;
  /** 电荷量（正为正电荷，负为负电荷） */
  charge: number;
}

/**
 * 磁场源配置
 */
export interface MagneticSource {
  /** 磁场源唯一标识 */
  id: string;
  /** 磁场源位置（物理坐标） */
  position: Vec2;
  /** 磁场强度（正值表示 N 极向外，负值表示 S 极向内） */
  strength: number;
  /** 磁场影响半径 */
  radius?: number;
}

/**
 * 带电粒子配置
 */
export interface ChargedParticle {
  /** 粒子唯一标识 */
  id: string;
  /** 粒子位置 */
  position: Vec2;
  /** 粒子速度 */
  velocity: Vec2;
  /** 粒子电荷量 */
  charge: number;
  /** 粒子质量 */
  mass: number;
}

/**
 * 场强结果（向量）
 */
export interface FieldVector {
  /** x 方向分量 */
  x: number;
  /** y 方向分量 */
  y: number;
  /** 场强大小（模） */
  magnitude: number;
}

/**
 * EMField 类
 * 管理电磁场源并计算场强和洛伦兹力
 */
export class EMField {
  private chargeSources: Map<string, ChargeSource> = new Map();
  private magneticSources: Map<string, MagneticSource> = new Map();
  private scaleFactor: number;

  /**
   * 创建电磁场实例
   * @param scaleFactor 缩放因子，用于调整场强计算（默认 1e-6）
   */
  constructor(scaleFactor: number = GAME_SCALE_FACTOR) {
    this.scaleFactor = scaleFactor;
  }

  /**
   * 添加电荷源
   * @param source 电荷源配置
   */
  addChargeSource(source: ChargeSource): void {
    this.chargeSources.set(source.id, source);
  }

  /**
   * 添加多个电荷源
   * @param sources 电荷源配置数组
   */
  addChargeSources(sources: ChargeSource[]): void {
    for (const source of sources) {
      this.addChargeSource(source);
    }
  }

  /**
   * 移除电荷源
   * @param id 电荷源标识
   */
  removeChargeSource(id: string): void {
    this.chargeSources.delete(id);
  }

  /**
   * 获取电荷源
   * @param id 电荷源标识
   */
  getChargeSource(id: string): ChargeSource | undefined {
    return this.chargeSources.get(id);
  }

  /**
   * 获取所有电荷源
   */
  getAllChargeSources(): ChargeSource[] {
    return Array.from(this.chargeSources.values());
  }

  /**
   * 添加磁场源
   * @param source 磁场源配置
   */
  addMagneticSource(source: MagneticSource): void {
    this.magneticSources.set(source.id, source);
  }

  /**
   * 添加多个磁场源
   * @param sources 磁场源配置数组
   */
  addMagneticSources(sources: MagneticSource[]): void {
    for (const source of sources) {
      this.addMagneticSource(source);
    }
  }

  /**
   * 移除磁场源
   * @param id 磁场源标识
   */
  removeMagneticSource(id: string): void {
    this.magneticSources.delete(id);
  }

  /**
   * 获取磁场源
   * @param id 磁场源标识
   */
  getMagneticSource(id: string): MagneticSource | undefined {
    return this.magneticSources.get(id);
  }

  /**
   * 获取所有磁场源
   */
  getAllMagneticSources(): MagneticSource[] {
    return Array.from(this.magneticSources.values());
  }

  /**
   * 计算某点的电场强度
   * 使用库仑定律: E = kQ/r²
   * @param position 目标位置
   * @returns 电场向量
   */
  calculateElectricField(position: Vec2): FieldVector {
    let totalEx = 0;
    let totalEy = 0;

    for (const source of this.chargeSources.values()) {
      const dx = position.x - source.position.x;
      const dy = position.y - source.position.y;
      const distanceSquared = dx * dx + dy * dy;
      const distance = Math.sqrt(Math.max(distanceSquared, MIN_DISTANCE * MIN_DISTANCE));

      // 库仑定律: E = kQ/r²
      // 方向：正电荷向外，负电荷向内
      const fieldMagnitude = (COULOMB_CONSTANT * this.scaleFactor * source.charge) / (distance * distance);

      // 计算方向向量（单位向量）
      const unitX = dx / distance;
      const unitY = dy / distance;

      // 正电荷场向外（与dx同向），负电荷场向内（与dx反向）
      totalEx += fieldMagnitude * unitX;
      totalEy += fieldMagnitude * unitY;
    }

    const magnitude = Math.sqrt(totalEx * totalEx + totalEy * totalEy);

    return {
      x: totalEx,
      y: totalEy,
      magnitude,
    };
  }

  /**
   * 计算某点的磁场强度（2D简化模型）
   * 磁场垂直于屏幕平面，返回 z 方向分量
   * @param position 目标位置
   * @returns 磁场强度（z 方向分量）
   */
  calculateMagneticField(position: Vec2): number {
    let totalBz = 0;

    for (const source of this.magneticSources.values()) {
      const dx = position.x - source.position.x;
      const dy = position.y - source.position.y;
      const distanceSquared = dx * dx + dy * dy;
      const distance = Math.sqrt(Math.max(distanceSquared, MIN_DISTANCE * MIN_DISTANCE));

      // 简化磁场模型：B 随距离衰减
      // 磁场源强度影响范围内保持相对稳定
      const effectiveRadius = source.radius ?? 100;

      if (distance <= effectiveRadius) {
        // 在影响范围内，磁场强度从中心向外衰减
        const decayFactor = 1 - (distance / effectiveRadius);
        totalBz += source.strength * decayFactor * this.scaleFactor;
      } else {
        // 在影响范围外，磁场快速衰减（类似磁偶极子，与距离平方成反比）
        totalBz += (source.strength * this.scaleFactor) * Math.pow(effectiveRadius / distance, 2);
      }
    }

    return totalBz;
  }

  /**
   * 计算洛伦兹力
   * 公式: F = qE + qv×B
   * 在 2D 中：v×B = (vy*Bz, -vx*Bz)
   * @param particle 带电粒子
   * @returns 力向量 { x, y }
   */
  calculateLorentzForce(particle: ChargedParticle): { x: number; y: number } {
    // 电场力: F_E = qE
    const electricField = this.calculateElectricField(particle.position);
    const electricForceX = particle.charge * electricField.x;
    const electricForceY = particle.charge * electricField.y;

    // 磁场力: F_B = qv×B
    // 在 2D 中，B 垂直于屏幕（z 方向）
    // v×B_z = (vy*Bz, -vx*Bz)
    const magneticField = this.calculateMagneticField(particle.position);
    const magneticForceX = particle.charge * particle.velocity.y * magneticField;
    const magneticForceY = -particle.charge * particle.velocity.x * magneticField;

    // 总洛伦兹力
    return {
      x: electricForceX + magneticForceX,
      y: electricForceY + magneticForceY,
    };
  }

  /**
   * 更新带电粒子运动状态
   * 根据洛伦兹力计算加速度并更新速度
   * @param particle 带电粒子
   * @param dt 时间增量（秒）
   * @returns 更新后的粒子状态
   */
  updateParticleMotion(particle: ChargedParticle, dt: number): ChargedParticle {
    const force = this.calculateLorentzForce(particle);

    // 加速度 a = F/m
    const accelerationX = force.x / particle.mass;
    const accelerationY = force.y / particle.mass;

    // 更新速度 v = v0 + a*dt
    const newVelocityX = particle.velocity.x + accelerationX * dt;
    const newVelocityY = particle.velocity.y + accelerationY * dt;

    // 更新位置 p = p0 + v*dt（简化，忽略加速度对位置的影响）
    const newPositionX = particle.position.x + particle.velocity.x * dt;
    const newPositionY = particle.position.y + particle.velocity.y * dt;

    return {
      ...particle,
      position: Vec2(newPositionX, newPositionY),
      velocity: Vec2(newVelocityX, newVelocityY),
    };
  }

  /**
   * 计算电场线起点
   * 正电荷周围场线向外发散
   * @param sourceId 电荷源标识
   * @param lineCount 场线数量
   * @param startRadius 起始半径（距电荷中心的距离）
   * @returns 场线起点数组
   */
  getElectricFieldLineStartPoints(sourceId: string, lineCount: number, startRadius: number): Vec2[] {
    const source = this.chargeSources.get(sourceId);
    if (!source) return [];

    const points: Vec2[] = [];
    const angleStep = (2 * Math.PI) / lineCount;

    for (let i = 0; i < lineCount; i++) {
      const angle = i * angleStep;
      const x = source.position.x + startRadius * Math.cos(angle);
      const y = source.position.y + startRadius * Math.sin(angle);
      points.push(Vec2(x, y));
    }

    return points;
  }

  /**
   * 沿电场线方向追踪下一个点
   * 用于绘制连续的场线
   * @param currentPoint 当前点位置
   * @param stepSize 步进大小
   * @returns 下一个点位置
   */
  traceElectricFieldLineStep(currentPoint: Vec2, stepSize: number): Vec2 {
    const field = this.calculateElectricField(currentPoint);

    if (field.magnitude === 0) {
      return currentPoint;
    }

    // 沿场方向移动
    const unitX = field.x / field.magnitude;
    const unitY = field.y / field.magnitude;

    return Vec2(
      currentPoint.x + unitX * stepSize,
      currentPoint.y + unitY * stepSize
    );
  }

  /**
   * 清除所有电荷源
   */
  clearChargeSources(): void {
    this.chargeSources.clear();
  }

  /**
   * 清除所有磁场源
   */
  clearMagneticSources(): void {
    this.magneticSources.clear();
  }

  /**
   * 清除所有源
   */
  clear(): void {
    this.clearChargeSources();
    this.clearMagneticSources();
  }

  /**
   * 获取电荷源数量
   */
  getChargeSourceCount(): number {
    return this.chargeSources.size;
  }

  /**
   * 获取磁场源数量
   */
  getMagneticSourceCount(): number {
    return this.magneticSources.size;
  }

  /**
   * 设置缩放因子
   */
  setScaleFactor(factor: number): void {
    this.scaleFactor = factor;
  }

  /**
   * 获取缩放因子
   */
  getScaleFactor(): number {
    return this.scaleFactor;
  }
}

// 导出常量和类型
export { COULOMB_CONSTANT, MIN_DISTANCE };